import * as Types from '@trello/graphql/generated';

import { useQuickLoad } from '@trello/quickload';
import type { TrelloQueryHookOptions, TrelloLazyQueryHookOptions, TrelloSuspenseQueryHookOptions } from '@trello/quickload';
import DocumentNode from '@trello/graphql/documentNode';
import * as Apollo from '@apollo/client';
export const PluginCapabilitiesDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"PluginCapabilities"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"idPlugin"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"ID"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"plugin"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"id"},"value":{"kind":"Variable","name":{"kind":"Name","value":"idPlugin"}}}],"directives":[{"kind":"Directive","name":{"kind":"Name","value":"client"}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"capabilities"}},{"kind":"Field","name":{"kind":"Name","value":"capabilitiesOptions"}},{"kind":"Field","name":{"kind":"Name","value":"name"}}]}}]}}]} as unknown as DocumentNode;
const defaultOptions = {"context":{"operationName":"PluginCapabilities","document":PluginCapabilitiesDocument}} as const;
export type PluginCapabilitiesQueryVariables = Types.Exact<{
  idPlugin: Types.Scalars['ID']['input'];
}>;


export type PluginCapabilitiesQuery = (
  { __typename: 'Query' }
  & { plugin?: Types.Maybe<(
    { __typename: 'Plugin' }
    & Pick<Types.Plugin, 'id' | 'capabilities' | 'capabilitiesOptions' | 'name'>
  )> }
);

/**
 * __usePluginCapabilitiesQuery__
 *
 * To run a query within a React component, call `usePluginCapabilitiesQuery` and pass it any options that fit your needs.
 * When your component renders, `usePluginCapabilitiesQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = usePluginCapabilitiesQuery({
 *   variables: {
 *      idPlugin: // value for 'idPlugin'
 *   },
 * });
 */
export function usePluginCapabilitiesQuery(
  baseOptions: TrelloQueryHookOptions<
    PluginCapabilitiesQuery,
    PluginCapabilitiesQueryVariables
  > &
    (
      | { variables: PluginCapabilitiesQueryVariables; skip?: boolean }
      | { skip: boolean }
    ),
) {
  const isQuickLoading = useQuickLoad({
    waitOn: baseOptions?.waitOn || ['None'],
    document: PluginCapabilitiesDocument,
    skip: baseOptions?.skip,
  });
  const options = {
    ...defaultOptions,
    ...baseOptions,
    skip: isQuickLoading || baseOptions?.skip,
  };
  const result = Apollo.useQuery<
    PluginCapabilitiesQuery,
    PluginCapabilitiesQueryVariables
  >(PluginCapabilitiesDocument, options);
  // reconstruct result because modifying the useQuery result actually changes apollo behavior because of memoization
  const trelloResult = { ...result };
  if (isQuickLoading) {
    trelloResult.loading = true;
  }

  return trelloResult;
}
export function usePluginCapabilitiesLazyQuery(
  baseOptions?: TrelloLazyQueryHookOptions<
    PluginCapabilitiesQuery,
    PluginCapabilitiesQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<
    PluginCapabilitiesQuery,
    PluginCapabilitiesQueryVariables
  >(PluginCapabilitiesDocument, options);
}
export function usePluginCapabilitiesSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | TrelloSuspenseQueryHookOptions<
        PluginCapabilitiesQuery,
        PluginCapabilitiesQueryVariables
      >,
) {
  const options =
    baseOptions === Apollo.skipToken
      ? baseOptions
      : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<
    PluginCapabilitiesQuery,
    PluginCapabilitiesQueryVariables
  >(PluginCapabilitiesDocument, options);
}
export type PluginCapabilitiesQueryHookResult = ReturnType<
  typeof usePluginCapabilitiesQuery
>;
export type PluginCapabilitiesLazyQueryHookResult = ReturnType<
  typeof usePluginCapabilitiesLazyQuery
>;
export type PluginCapabilitiesSuspenseQueryHookResult = ReturnType<
  typeof usePluginCapabilitiesSuspenseQuery
>;
export type PluginCapabilitiesQueryResult = Apollo.QueryResult<
  PluginCapabilitiesQuery,
  PluginCapabilitiesQueryVariables
>;
